import { useForm } from "@inertiajs/react";
import { useEffect, useState } from "react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";

interface Firm {
  id: number;
  name: string;
}

export interface CandidateFormData {
  first_name: string;
  last_name: string;
  middle_initial: string;
  preferred_first_name: string;
  email: string;
  phone: string;
  firm_id: string;
  firm_name: string;
  candidate_type: string;
  jd_year: string;
  law_school: string;
  undergrad: string;
  practice_area: string;
  specialties: string;
  location: string;
  admitted: string;
  languages: string;
  degrees: string;
  honors: string;
  origin_firm: string;
  profile_url: string;
  linkedin_url: string;
  pipeline_stage: string;
  priority: string;
  source: string;
  notes: string;
  intake_date: string;
  leopard_id: string;
  resume_blob_id: string;
}

interface Props {
  initialValues?: Partial<CandidateFormData>;
  resumeBlobId?: string;
  firms: Firm[];
  submitUrl: string;
  method: "post" | "put" | "patch";
  submitLabel: string;
  cancelUrl: string;
}

const PIPELINE_STAGES = [
  { value: "new_lead", label: "New Lead" },
  { value: "intake_scheduled", label: "Intake Scheduled" },
  { value: "resume_editing", label: "Resume Editing" },
  { value: "package_pending", label: "Package Pending" },
  { value: "submitted", label: "Submitted" },
  { value: "screener", label: "Screener" },
  { value: "full_round", label: "Full Round" },
  { value: "offer_placed", label: "Offer / Placed" },
];

const PRIORITIES = [
  { value: "high", label: "High" },
  { value: "normal", label: "Normal" },
  { value: "low", label: "Low" },
];

const SOURCES = [
  { value: "leopard", label: "Leopard" },
  { value: "linkedin", label: "LinkedIn" },
  { value: "inbound", label: "Inbound" },
  { value: "referral", label: "Referral" },
  { value: "cold_email_response", label: "Cold Email Response" },
];

function FieldError({ errors, field }: { errors: Record<string, string[]>; field: string }) {
  const msgs = errors[field];
  if (!msgs?.length) return null;
  return <p className="text-xs text-red-600 mt-1">{msgs[0]}</p>;
}

export default function CandidateForm({ initialValues = {}, resumeBlobId, firms, submitUrl, method, submitLabel, cancelUrl }: Props) {
  const defaults: CandidateFormData = {
    first_name: "",
    last_name: "",
    middle_initial: "",
    preferred_first_name: "",
    email: "",
    phone: "",
    firm_id: "",
    firm_name: "",
    candidate_type: "",
    jd_year: "",
    law_school: "",
    undergrad: "",
    practice_area: "",
    specialties: "",
    location: "",
    admitted: "",
    languages: "",
    degrees: "",
    honors: "",
    origin_firm: "",
    profile_url: "",
    linkedin_url: "",
    pipeline_stage: "new_lead",
    priority: "normal",
    source: "leopard",
    notes: "",
    intake_date: "",
    leopard_id: "",
    resume_blob_id: "",
    ...initialValues,
  };

  const { data, setData, submit, processing, errors } = useForm<CandidateFormData>(defaults);

  // Firm picker mode. "linked" = a canonical Firm row is selected (firm_id set,
  // matching keys off the Firm). "unlisted" = firm isn't in the directory, so
  // the free-text firm_name field is revealed. "none" = no firm. Edit-form
  // prefill: a candidate with firm_name but no firm_id opens in "unlisted".
  const FIRM_NONE = "__none__";
  const FIRM_UNLISTED = "__unlisted__";
  const [firmMode, setFirmMode] = useState<"linked" | "unlisted" | "none">(
    defaults.firm_id ? "linked" : defaults.firm_name ? "unlisted" : "none",
  );
  const firmSelectValue =
    firmMode === "linked" ? data.firm_id : firmMode === "unlisted" ? FIRM_UNLISTED : FIRM_NONE;

  function handleFirmChange(value: string) {
    if (value === FIRM_NONE) {
      setFirmMode("none");
      setData((d) => ({ ...d, firm_id: "", firm_name: "" }));
    } else if (value === FIRM_UNLISTED) {
      setFirmMode("unlisted");
      setData("firm_id", "");
    } else {
      // A canonical Firm was picked — its name drives matching, so clear the
      // now-redundant free-text firm_name.
      setFirmMode("linked");
      setData((d) => ({ ...d, firm_id: value, firm_name: "" }));
    }
  }

  useEffect(() => {
    if (resumeBlobId) setData("resume_blob_id", resumeBlobId);
  }, [resumeBlobId]);
  const errorMap = errors as Record<string, string[]>;

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    submit(method, submitUrl);
  }

  function field(id: keyof CandidateFormData, label: string, type: string = "text") {
    return (
      <div>
        <Label htmlFor={id}>{label}</Label>
        <Input
          id={id}
          type={type}
          value={data[id]}
          onChange={(e) => setData(id, e.target.value)}
          className="mt-1"
        />
        <FieldError errors={errorMap} field={id} />
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-8">
      {/* Name */}
      <section>
        <h3 className="text-sm font-semibold text-gray-700 mb-3 uppercase tracking-wide">Name</h3>
        <div className="grid grid-cols-2 gap-4">
          {field("first_name", "First Name")}
          {field("last_name", "Last Name")}
          {field("middle_initial", "Middle Initial")}
          {field("preferred_first_name", "Preferred First Name")}
        </div>
      </section>

      {/* Contact */}
      <section>
        <h3 className="text-sm font-semibold text-gray-700 mb-3 uppercase tracking-wide">Contact</h3>
        <div className="grid grid-cols-2 gap-4">
          {field("email", "Email", "email")}
          {field("phone", "Phone", "tel")}
        </div>
      </section>

      {/* Firm */}
      <section>
        <h3 className="text-sm font-semibold text-gray-700 mb-3 uppercase tracking-wide">Firm</h3>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label htmlFor="firm_id">Current Firm</Label>
            <Select value={firmSelectValue} onValueChange={handleFirmChange}>
              <SelectTrigger className="mt-1">
                <SelectValue placeholder="Select firm..." />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={FIRM_NONE}>None</SelectItem>
                {firms.map((f) => (
                  <SelectItem key={f.id} value={String(f.id)}>{f.name}</SelectItem>
                ))}
                <SelectItem value={FIRM_UNLISTED}>Not listed — enter manually</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {firmMode === "unlisted" && field("firm_name", "Current Firm Name (Unlisted)")}
        </div>
      </section>

      {/* Professional */}
      <section>
        <h3 className="text-sm font-semibold text-gray-700 mb-3 uppercase tracking-wide">Professional</h3>
        <div className="grid grid-cols-2 gap-4">
          {field("candidate_type", "Type (e.g. ASSOCIATE)")}
          {field("jd_year", "JD Year", "number")}
          {field("practice_area", "Practice Area")}
          {field("specialties", "Specialties")}
          {field("location", "Location")}
          {field("admitted", "Admitted")}
          {field("law_school", "Law School")}
          {field("undergrad", "Undergrad")}
          {field("degrees", "Degrees")}
          {field("honors", "Honors")}
          {field("languages", "Languages")}
          {field("origin_firm", "Origin Firm")}
        </div>
      </section>

      {/* Links */}
      <section>
        <h3 className="text-sm font-semibold text-gray-700 mb-3 uppercase tracking-wide">Links</h3>
        <div className="grid grid-cols-2 gap-4">
          {field("profile_url", "Leopard Profile URL")}
          {field("linkedin_url", "LinkedIn URL")}
          {field("leopard_id", "Leopard ID")}
        </div>
      </section>

      {/* Workflow */}
      <section>
        <h3 className="text-sm font-semibold text-gray-700 mb-3 uppercase tracking-wide">Workflow</h3>
        <div className="grid grid-cols-3 gap-4">
          <div>
            <Label>Pipeline Stage</Label>
            <Select value={data.pipeline_stage} onValueChange={(v) => setData("pipeline_stage", v)}>
              <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
              <SelectContent>
                {PIPELINE_STAGES.map((s) => (
                  <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Priority</Label>
            <Select value={data.priority} onValueChange={(v) => setData("priority", v)}>
              <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
              <SelectContent>
                {PRIORITIES.map((p) => (
                  <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Source</Label>
            <Select value={data.source} onValueChange={(v) => setData("source", v)}>
              <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
              <SelectContent>
                {SOURCES.map((s) => (
                  <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
        <div className="mt-4">
          {field("intake_date", "Intake Date", "date")}
        </div>
      </section>

      {/* Notes */}
      <section>
        <h3 className="text-sm font-semibold text-gray-700 mb-3 uppercase tracking-wide">Notes</h3>
        <Textarea
          value={data.notes}
          onChange={(e) => setData("notes", e.target.value)}
          rows={4}
          placeholder="Internal notes..."
        />
      </section>

      <div className="flex items-center gap-3 pt-2">
        <Button type="submit" disabled={processing}>{submitLabel}</Button>
        <a href={cancelUrl} className="inline-flex items-center justify-center text-sm font-medium text-gray-700 border-2 border-gray-400 bg-white hover:bg-gray-50 hover:border-gray-600 rounded-md px-4 py-2 transition-colors">Cancel</a>
      </div>
    </form>
  );
}
